import React from "react";
import {
  Box,
  Button, CircularProgress,
  Typography,
  useTheme
} from "@mui/material";
import { tokens } from "../../theme";
import faceLaughBeam from "../../scenes/images/fa-icons/face-laugh-beam.svg";
import usePayment from "../../hooks/payment_hook";

interface PricingOptionProps {
  title: string;
  price: string;
  features: string[];
  onPurchase: (productId: string, billingPeriod: "monthly" | "yearly") => void;
  billingPeriod: "monthly" | "yearly";
  productId: string;
  isPaymentLoading?: boolean;
}

const PricingOption: React.FC<PricingOptionProps> = ({
                                                       title,
                                                       price,
                                                       features,
                                                       onPurchase,
                                                       billingPeriod,
                                                       productId,
                                                       isPaymentLoading
                                                     }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);


  return (
    <div className="w-full sm:w-3/4 md:w-1/2 px-4">
      <div
        className="bg-white shadow-pricing p-10 md:px-8 lg:py-10 lg:px-6 xl:p-10 text-center rounded-2xl relative z-10 mb-10">
        <h3 className="text-black font-bold text-2xl mb-5">{title}</h3>
        <div className="flex justify-center items-end">
          <h2 className="font-bold text-black text-[42px] mb-2">
            {price}
            <span className="text-lg font-medium text-body-color">
              {billingPeriod === "yearly" ? " /month, billed yearly" : " /month"}
            </span>
          </h2>
        </div>
        <ul className="mb-8 pricing-list">
          {features.map((feature, index) => (
            <li key={index} className="font-medium text-base text-body-color mb-3 flex flex-row gap-2 items-center">
              <img src={faceLaughBeam} className="pricing-list-icon" />
              <p className="text-left ml-2">{feature}</p>
            </li>
          ))}
        </ul>
        {isPaymentLoading ? <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 1.5,
            borderRadius: 2,
            width: "100%",
            textTransform: "none",
            fontSize: "1rem"
          }}
        >
          <CircularProgress />
        </Box> : <Button
          variant="contained"
          disabled={isPaymentLoading}
          color="primary"
          onClick={() => onPurchase(productId, billingPeriod)}
          sx={{
            p: 1.5,
            borderRadius: 2,
            width: "100%",
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 500,
            "&:hover": {
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)"
            }
          }}
        >
          Purchase Now
        </Button>}
        <div className="absolute right-0 top-0 z-[-1]">
          <svg width="37" height="154" viewBox="0 0 37 154" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <rect opacity="0.1" x="17.5237" y="78.0005" width="24.7822" height="24.7822" rx="4"
                  transform="rotate(45 17.5237 78.0005)" fill="#FF0000" />
            <rect opacity="0.3" x="47.585" y="92.9392" width="41.1567" height="45.1468" rx="5"
                  transform="rotate(45 47.585 92.9392)" fill="#FF0000" />
          </svg>
        </div>
        <div className="absolute left-0 bottom-0 z-[-1]">
          <svg width="229" height="182" viewBox="0 0 229 182" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <rect opacity="0.05" x="-0.639709" y="21.1696" width="46.669" height="45.7644" rx="10"
                  transform="rotate(45 -0.639709 21.1696)" fill="url(#paint0_linear_25:114)" />
            <rect opacity="0.05" x="40.1691" y="0.000488281" width="34.3355" height="33.67" rx="5"
                  transform="rotate(45 40.1691 0.000488281)" fill="url(#paint1_linear_25:114)" />
            <rect opacity="0.07" x="60.9458" y="42.1696" width="237" height="380.347" rx="22"
                  transform="rotate(45 60.9458 42.1696)" fill="url(#paint2_linear_25:114)" />
            <defs>
              <linearGradient id="paint0_linear_25:114" x1="29.5176" y1="14.0581" x2="14.35"
                              y2="82.5021" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FF0000" />
                <stop offset="0.822917" stopColor="#FF0000" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="paint1_linear_25:114" x1="62.3565" y1="-5.23156" x2="51.1974"
                              y2="45.1244" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FF0000" />
                <stop offset="0.822917" stopColor="#FF0000" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="paint2_linear_25:114" x1="214.094" y1="-16.9334" x2="120.415"
                              y2="348.232" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FF0000" />
                <stop offset="0.822917" stopColor="#FF0000" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default PricingOption;
